import { useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useSubscribeNewsletter, useUnsubscribeNewsletter } from "@/hooks/useNewsletter";
import { useCategories } from "@/hooks/useCategories";
import type { CategorieDto } from "@/types/categorie.types";
import axios from "axios";

export function FollowSection() {
  const { t, locale } = useI18n();
  const [email, setEmail] = useState("");
  const [modeDesabonnement, setModeDesabonnement] = useState(false);
  const [categorieIds, setCategorieIds] = useState<number[]>([]);
  const subscribeMutation = useSubscribeNewsletter();
  const unsubscribeMutation = useUnsubscribeNewsletter();

  const { data: categoriesData } = useCategories();
  const categories: CategorieDto[] = Array.isArray(categoriesData) ? categoriesData : [];

  const toggleCategorie = (id: number) => {
    setCategorieIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };

  function subscribe(canal: string) {
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error(t("form.email"));
      return;
    }
    subscribeMutation.mutate(
      { email: trimmed, canal, langue: locale, categorieIds },
      {
        onSuccess: () => {
          toast.success(t("newsletter.success"));
          setEmail("");
          setCategorieIds([]);
        },
        onError: (err) => {
          if (axios.isAxiosError(err)) {
            const status = err.response?.status;
            if (status === 409) {
              toast.error(t("newsletter.already_subscribed"));
            } else if (status === 400) {
              toast.error(t("newsletter.invalid_email"));
            } else {
              toast.error(t("newsletter.error"));
            }
          } else {
            toast.error(t("newsletter.error"));
          }
        },
      }
    );
  }

  function unsubscribe() {
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error(t("form.email"));
      return;
    }
    unsubscribeMutation.mutate(trimmed, {
      onSuccess: () => {
        toast.success(t("newsletter.unsubscribe.success"));
        setEmail("");
      },
    });
  }

  return (
    <section className="rounded-lg border border-border bg-foreground text-background overflow-hidden">
      <div className="grid gap-8 p-8 md:p-12 md:grid-cols-2 items-center">
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight">
            {t("section.follow")}
          </h2>
          <p className="mt-3 max-w-md text-sm text-background/70 leading-relaxed">
            {t("section.follow.desc")}
          </p>
        </div>
        <div className="space-y-3">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (modeDesabonnement) unsubscribe();
              else subscribe("EMAIL");
            }}
          >
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("form.email")}
                // Fond blanc opaque + texte foncé : lisible dans tous les cas,
                // contrairement à un fond translucide + texte blanc (invisible
                // dès que la transparence ne s'applique pas comme prévu).
                className="w-full flex-1 rounded-md border border-background/20 bg-white px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-verified"
              />
              <button
                type="submit"
                disabled={subscribeMutation.isPending || unsubscribeMutation.isPending}
                className="w-full rounded-md bg-verified px-4 py-2.5 text-sm font-semibold text-verified-foreground hover:opacity-90 disabled:opacity-50 sm:w-auto"
              >
                {subscribeMutation.isPending || unsubscribeMutation.isPending
                  ? "…"
                  : modeDesabonnement
                  ? t("newsletter.unsubscribe")
                  : t("cta.subscribe")}
              </button>
            </div>

            {!modeDesabonnement && categories.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-xs text-background/70">{t("newsletter.categoriesHint")}</div>
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCategorie(c.id)}
                      className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                        categorieIds.includes(c.id)
                          ? "bg-verified text-verified-foreground"
                          : "border border-background/20 text-background/80 hover:bg-background/10"
                      }`}
                    >
                      {locale === "fr" ? c.libelleFr : c.libelleEn}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setModeDesabonnement((v) => !v)}
              className="text-xs text-background/60 underline hover:text-background/90"
            >
              {modeDesabonnement ? t("newsletter.backToSubscribe") : t("newsletter.unsubscribeLink")}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
